import { JOB_STATUS } from './constants';
import { parseImportSettingsFromJob } from './importSettings';

// Adapter layer: the backend (`self_importer`) returns machine-readable keys
// (`origin`, `status`/`status_group`, string `author`, `total_records`, flat
// pagination). The dashboard components consume a view model instead. Keep this
// mapping here so `ImporterService` stays a thin transport wrapper and the UI
// never has to know about the raw contract.

// Option A (TBR-5907): the `GET /jobs/` list does not include the uploaded
// filename or feed URL yet, so the "Import" column subtitle is derived from the
// `origin` key. Option B: once the backend adds `files`/`filename` to
// `serialize_job`, use the real name here instead of an empty label.
// TODO(TBR-5907 / Option B): populate origin.label with the real filename/URL
// when the backend exposes it.
const ORIGIN_TYPE = {
  generic_file: 'file',
  vivareal: 'link',
  imovelweb: 'api',
  casa_mineira: 'api',
  zonaprop: 'api',
  inmuebles24: 'api',
  plusvalia: 'api',
  urbania: 'api',
  loft: 'file',
  kenlo: 'file',
  easybroker: 'api',
};

// Map the numeric status enum to the string status group as a fallback for when
// the backend omits `status_group` (it normally sends it).
const STATUS_INT_TO_GROUP = {
  0: JOB_STATUS.IN_PROGRESS, // pending
  1: JOB_STATUS.IN_PROGRESS, // running
  2: JOB_STATUS.DONE, // finished
  3: JOB_STATUS.ERROR, // error
};

// `live_info.data.status` and the list's `status_display` are human-ish
// strings ('finished', 'running', ...), not the status group, so map them too.
const STATUS_DISPLAY_TO_GROUP = {
  finished: JOB_STATUS.DONE,
  running: JOB_STATUS.IN_PROGRESS,
  pending: JOB_STATUS.IN_PROGRESS,
  error: JOB_STATUS.ERROR,
};

const resolveStatus = (job) =>
  job.status_group ?? STATUS_INT_TO_GROUP[job.status] ?? null;

const normalizeRecords = (source) => ({
  total: source.total_records ?? 0,
  processed: source.processed_records ?? 0,
  errors: source.errors ?? 0,
});

// Preserve intentional nulls from the backend: null means "not available", 0 is
// a real zero. Only map undefined to null for optional keys.
const mapNullable = (value) => (value === undefined ? null : value);

const normalizeSkippedByReason = (skippedByReason) =>
  skippedByReason
    ? {
        dbDuplicate: mapNullable(skippedByReason.db_duplicate),
        fileDuplicate: mapNullable(skippedByReason.file_duplicate),
        missingName: mapNullable(skippedByReason.missing_name),
        missingContactInfo: mapNullable(skippedByReason.missing_contact_info),
        missingPropertyReference: mapNullable(
          skippedByReason.missing_property_reference
        ),
        propertyNotFound: mapNullable(skippedByReason.property_not_found),
        imageUrlExpired: mapNullable(skippedByReason.image_url_expired),
        existingPropertyWithoutPrice: mapNullable(
          skippedByReason.existing_property_without_price
        ),
      }
    : null;

const normalizeErrorsByReason = (errorsByReason) =>
  errorsByReason
    ? {
        inactiveWithoutPrice: mapNullable(
          errorsByReason.inactive_without_price
        ),
        unmappedStatusWithoutPrice: mapNullable(
          errorsByReason.unmapped_status_without_price
        ),
        missingStatus: mapNullable(errorsByReason.missing_status),
        unknownStatus: mapNullable(errorsByReason.unknown_status),
        missingParentDevelopment: mapNullable(
          errorsByReason.missing_parent_development
        ),
        parentDevelopmentNotFound: mapNullable(
          errorsByReason.parent_development_not_found
        ),
        other: mapNullable(errorsByReason.other),
      }
    : null;

export const normalizeResults = (results) => {
  if (results == null) return null;

  return {
    entityType: results.entity_type ?? null,
    total: mapNullable(results.total),
    processed: mapNullable(results.processed),
    imported: mapNullable(results.imported),
    created: mapNullable(results.created),
    updated: mapNullable(results.updated),
    skipped: mapNullable(results.skipped),
    errors: mapNullable(results.errors),
    alerts: mapNullable(results.alerts),
    byEntity: Array.isArray(results.by_entity)
      ? results.by_entity.map((item) => ({
          entityType: item.entity_type ?? null,
          processed: mapNullable(item.processed),
          imported: mapNullable(item.imported),
          skipped: mapNullable(item.skipped),
          updated: mapNullable(item.updated),
          errors: mapNullable(item.errors),
          skippedByReason: normalizeSkippedByReason(item.skipped_by_reason),
          errorsByReason: normalizeErrorsByReason(item.errors_by_reason),
        }))
      : null,
    skippedByReason: normalizeSkippedByReason(results.skipped_by_reason),
  };
};

export const normalizeValidation = (validation) => {
  if (validation == null) return null;

  return {
    valid: mapNullable(validation.valid),
    reasons: validation.reasons ?? [],
  };
};

export const normalizeJob = (job) => ({
  id: job.id,
  entityType: job.entity_type ?? null,
  name: job.origin_display ?? '',
  origin: {
    type: ORIGIN_TYPE[job.origin] ?? 'file',
    label: '',
  },
  status: resolveStatus(job),
  elements: job.elements ?? [],
  author: { name: job.author ?? '', date: job.date ?? null },
  records: normalizeRecords(job),
  results: normalizeResults(job.results),
  validation: normalizeValidation(job.validation),
  policies: parseImportSettingsFromJob(job),
});

// Adapt the `live_info` block from `GET /jobs/{id}/` for polling in-progress
// jobs. Returns null when there is nothing to merge (no live_info). `status`
// and `records` are null when absent so the reducer can skip stale overwrites.
export const normalizeLiveInfo = (response) => {
  const live = response?.live_info?.data;
  if (!live) return null;
  return {
    id: live.import_id,
    status: STATUS_DISPLAY_TO_GROUP[live.status] ?? null,
    records: live.total_records != null ? normalizeRecords(live) : null,
  };
};

export const normalizeJobsResponse = (response) => {
  const payload = response ?? {};
  return {
    jobs: (payload.jobs ?? []).map(normalizeJob),
    counts: payload.counts ?? { done: 0, in_progress: 0, error: 0 },
    pagination: {
      total_items: payload.total ?? 0,
      page_size: payload.page_size ?? 10,
    },
  };
};

export const normalizeValueMappings = (response) => ({
  agents: response?.agents ?? null,
  lead_status: response?.lead_status ?? null,
  readyForStart: Boolean(response?.ready_for_start),
});

// A block in `default` mode has no source values to map: the whole file gets a
// single fallback, so the payload carries only that default.
export const buildAgentMappingsBody = (block, draftMappings, defaultUserId) => {
  if (block?.mode === 'default') {
    return { default_user_id: defaultUserId ?? null };
  }
  return {
    mappings: (block?.source_values ?? []).map((source) => ({
      source_value: source.normalized,
      target_user_id: draftMappings[source.normalized],
    })),
  };
};

export const buildLeadStatusMappingsBody = (
  block,
  draftMappings,
  defaultLeadStatusId
) => {
  if (block?.mode === 'default') {
    return { default_lead_status_id: defaultLeadStatusId ?? null };
  }

  // Empty source rows are the fallback for records with no value, so they
  // travel as `default_lead_status_id` rather than a mapping with "".
  const mappings = [];
  let emptyRowTarget;
  (block?.source_values ?? []).forEach((source) => {
    const sourceValue = source.normalized ?? '';
    if (sourceValue === '') {
      emptyRowTarget = draftMappings[''];
      return;
    }
    mappings.push({
      source_value: sourceValue,
      target_lead_status_id: draftMappings[sourceValue],
    });
  });

  if (emptyRowTarget != null) {
    return { mappings, default_lead_status_id: emptyRowTarget };
  }
  return { mappings };
};
